/**
 * React Query hook for the current operator's eligible permit types (GET /permits/eligible-permits).
 * Used by usePermitApplications for visibility filtering and by other consumers needing the permit types list.
 * @module usePermitTypes
 * @since 2025-01
 */

import { useQuery } from "@tanstack/react-query";
import { getPermitTypesByOperatorId } from "../services/permits";

export const PERMIT_TYPES_QUERY_KEY = ["permit-types"];

/**
 * Fetches the current operator's eligible permit types.
 *
 * @param {Object} [queryOptions] - React Query options (e.g. enabled)
 * @returns {Object} React Query result { data, isLoading, isFetching, error, refetch }. data is the permit types array.
 */
/**
 * Shared query definition so an imperative `queryClient.fetchQuery` reuses the hook's
 * cache entry and freshness window rather than declaring a second one.
 */
export const permitTypesQueryOptions = {
	queryKey: PERMIT_TYPES_QUERY_KEY,
	queryFn: async () => {
		const response = await getPermitTypesByOperatorId();
		return response?.data?.data ?? response?.data ?? [];
	},
	staleTime: 5 * 60 * 1000, // 5 minutes
	cacheTime: 10 * 60 * 1000, // 10 minutes
};

export const usePermitTypes = (queryOptions = {}) =>
	useQuery({
		...permitTypesQueryOptions,
		retry: 2,
		refetchOnWindowFocus: false,
		...queryOptions,
	});

export default usePermitTypes;
